import { FormikProps } from "formik"
import { useRef } from "react"
import { useNavigate, useParams } from "react-router-dom"

import { ErrorDisplay, NoDataError, SuccessDisplay } from "../../../../../../core/api/errorTypes"
import { NoDataFallback, QueryErrorBoundary } from "../../../../../../core/api/components/QueryErrorBoundary"
import { queryKeyFor, useApiQuery } from "../../../../../../core/api/useApiQuery"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { COMPANY_ADMIN_ACCOUNT_ADMINS_ROUTE, COMPANY_ADMIN_ROUTE } from "../../../../../../constants/routes"
import { StandardLoadingButton } from "../../../../../../shared/components/buttons/LoadingButton"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { LoadingSpinner } from "../../../../../../shared/components/loading/LoadingSpinner"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { selectUserId } from "../../../../../login/loginSelectors"
import { UserType } from "../../../../../login/loginTypes"
import { useAppSelector } from "../../../../../store/storeHooks"
import { AdministratorForm } from "../../../../../users/administrators/components/AdministratorForm"
import { AdministratorFormValues } from "../../../../../users/administrators/administratorFormTypes"
import {
    toAdministratorFormValues,
    toUpdateAdministratorFields,
} from "../../../../../users/administrators/utils/administratorBody"
import { GetAdminDetailsResponse, getAdminDetails, updateAdmin, UserQuery } from "../../../../../users/userApi"
import { UpdateCompanyAdmin, UserActionType } from "../../../../../users/userTypes"
import { CompanyAdminsQuery } from "../adminsApi"

function selectCompanyAdmin({ admin }: GetAdminDetailsResponse) {
    if (admin.type !== UserType.COMPANY) throw new NoDataError()

    return {
        userId: admin.user.id,
        initialValues: toAdministratorFormValues(admin),
        history: admin.history,
        companyDetails: admin.companyDetails,
    }
}

export function EditCompanyAdminPage() {
    const navigate = useNavigate()
    const { userId: userIdParam } = useParams<{ userId?: string }>()
    const userId = Number(userIdParam)
    const isValidUserId = Number.isFinite(userId)

    const currentUserId = useAppSelector(selectUserId)

    const formRef = useRef<FormikProps<AdministratorFormValues>>(null)

    const adminResult = useApiQuery(getAdminDetails, {
        queryName: UserQuery.ADMIN_DETAILS,
        dependencies: { userId },
        enabled: isValidUserId,
        select: selectCompanyAdmin,
        errorDisplay: ErrorDisplay.NONE,
        useErrorBoundary: true,
    })

    const { mutateAsync: callUpdateAdmin, isLoading: isUpdating } = useApiMutation(updateAdmin, {
        successDisplay: SuccessDisplay.SNACKBAR,
        async onSuccess(params, _, context) {
            await context.queryClient.invalidateQueries({ queryKey: [CompanyAdminsQuery.ADMINS_FOR_COMPANY] })
            await context.queryClient.invalidateQueries({
                queryKey: queryKeyFor(UserQuery.ADMIN_DETAILS, { userId: params.userId }),
            })

            navigate(COMPANY_ADMIN_ACCOUNT_ADMINS_ROUTE)
        },
    })

    const handleSaveClick = () => {
        formRef.current?.submitForm()
    }

    async function handleSubmit(values: AdministratorFormValues) {
        if (!adminResult.data) return

        const admin: UpdateCompanyAdmin = {
            type: UserType.COMPANY,
            ...toUpdateAdministratorFields(values),
            companyDetails: adminResult.data.companyDetails,
        }

        await callUpdateAdmin({
            userId: adminResult.data.userId,
            admin,
            actions: values.resendInvitation ? [{ type: UserActionType.SEND_EMAIL_INVITATION }] : undefined,
        })
    }

    return (
        <>
            <LocalizedStrict id="administrators-edit-page-title" attrs={{ title: true }}>
                <PageHeader
                    page={Page.COMPANY_EDIT_ADMIN}
                    title="Edit administrator"
                    rightElement={
                        <LocalizedStrict id="administrators-edit-page-save-button">
                            <StandardLoadingButton
                                type="submit"
                                onClick={handleSaveClick}
                                loading={isUpdating}
                                disabled={!adminResult.data}
                            >
                                Save
                            </StandardLoadingButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="company-nav-item-home">
                            <BreadcrumbLink to={COMPANY_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="company-nav-item-account">
                            <Breadcrumb>Account</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="company-admin-nav-item-admins">
                            <BreadcrumbLink to={COMPANY_ADMIN_ACCOUNT_ADMINS_ROUTE}>Administrators</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="administrators-edit-breadcrumb">
                            <Breadcrumb>Edit administrator</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <QueryErrorBoundary dependencies={{ userId }}>
                {!isValidUserId && <NoDataFallback />}
                {isValidUserId &&
                    (adminResult.data ? (
                        <AdministratorForm
                            ref={formRef}
                            adminType={UserType.COMPANY}
                            isEditMode
                            isOwnProfile={adminResult.data.userId === currentUserId}
                            initialValues={adminResult.data.initialValues}
                            history={adminResult.data.history}
                            onSubmit={handleSubmit}
                        />
                    ) : (
                        <LoadingSpinner />
                    ))}
            </QueryErrorBoundary>
        </>
    )
}
